"use client";

import * as React from "react";
import { arEG, enUS, es, fr } from "date-fns/locale";
import type { Locale } from "date-fns";

import { cn } from "@/lib/utils";

import { Button } from "../Button";
// @ts-expect-error - Icon is a JSX component without TypeScript definitions
import { Icon } from "../../UIElements/Icon";
import { Calendar } from "../Calendar";
import { Label } from "../Label";
import { Popover, PopoverContent, PopoverTrigger } from "../Popover";

interface DatePickerProps {
  value?: Date;
  onChange?: (date: Date | undefined) => void;
  placeholder?: string;
  disabled?: boolean;
  error?: React.ReactNode | boolean;
  className?: string;
  label?: string;
  id?: string;
  displayLocale?: string | string[];
  "aria-describedby"?: string;
}

const calendarLocales: Record<string, Locale> = {
  ar: arEG,
  "ar-eg": arEG,
  en: enUS,
  "en-us": enUS,
  es,
  fr,
};

function resolveCalendarLocale(displayLocale?: string | string[]) {
  const requestedLocales = Array.isArray(displayLocale)
    ? displayLocale
    : displayLocale
      ? [displayLocale]
      : [];

  for (const requestedLocale of requestedLocales) {
    const normalizedLocale = requestedLocale.toLowerCase();
    const exactLocale = calendarLocales[normalizedLocale];
    if (exactLocale) return exactLocale;

    const baseLocale = calendarLocales[normalizedLocale.split("-")[0]];
    if (baseLocale) return baseLocale;
  }

  return undefined;
}

function DatePicker({
  value,
  onChange,
  placeholder = "Select date",
  disabled = false,
  error = false,
  className = "",
  label,
  id,
  displayLocale,
  "aria-describedby": ariaDescribedBy,
  ...props
}: DatePickerProps) {
  const [open, setOpen] = React.useState(false);
  const generatedId = React.useId();
  const controlId = id ?? `date-picker-${generatedId}`;
  const documentLocale =
    typeof document === "undefined" ? undefined : document.documentElement.lang;
  const effectiveDisplayLocale = displayLocale || documentLocale || undefined;
  const calendarLocale = resolveCalendarLocale(effectiveDisplayLocale);

  const handleSelect = (date: Date | undefined) => {
    onChange?.(date);
    setOpen(false);
  };

  return (
    <div className="flex flex-col gap-3">
      {label && (
        <Label htmlFor={controlId} className="px-1">
          {label}
        </Label>
      )}
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outlineLight"
            id={controlId}
            disabled={disabled}
            aria-invalid={Boolean(error) || undefined}
            aria-describedby={ariaDescribedBy}
            className={cn(
              "h-9 w-full justify-between font-normal",
              error && "border-destructive focus-visible:ring-destructive",
              className,
            )}
            {...props}
          >
            {value
              ? value.toLocaleDateString(effectiveDisplayLocale)
              : placeholder}
            <Icon name="ChevronDown" className="h-4 w-4 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto overflow-hidden p-0" align="start">
          <Calendar
            mode="single"
            selected={value}
            defaultMonth={value}
            captionLayout="dropdown"
            onSelect={handleSelect}
            locale={calendarLocale}
            formatters={
              effectiveDisplayLocale
                ? {
                    formatMonthDropdown: (date) =>
                      date
                        ? date.toLocaleString(effectiveDisplayLocale, {
                            month: "short",
                          })
                        : "",
                  }
                : undefined
            }
          />
        </PopoverContent>
      </Popover>
    </div>
  );
}

export { DatePicker };
